import React, { useState } from 'react';
import { Mail, Instagram, Twitter, Linkedin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactInfo } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (MOCK)
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const socialLinks = [
    { icon: Instagram, label: 'Instagram', value: contactInfo.social.instagram, href: 'https://instagram.com' },
    { icon: Twitter, label: 'Twitter', value: contactInfo.social.twitter, href: 'https://twitter.com' },
    { icon: Linkedin, label: 'LinkedIn', value: 'Aaron White', href: `https://${contactInfo.social.linkedin}` }
  ];

  return (
    <div className="min-h-screen pt-16 bg-[#1a1a1a]">
      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-b border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-3 uppercase">
            Get In Touch
          </h1>
          <p className="text-sm md:text-base text-gray-400 font-light tracking-wider">
            Let's discuss your next project
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#2a2a2a] border border-gray-800 p-8 md:p-10">
            <h2 className="text-3xl font-bold text-white mb-6 uppercase tracking-wider">Send a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-xs text-gray-400 uppercase tracking-widest mb-2">
                  Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-600"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs text-gray-400 uppercase tracking-widest mb-2">
                  Email *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-600"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs text-gray-400 uppercase tracking-widest mb-2">
                  Subject *
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  className="w-full bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-600"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs text-gray-400 uppercase tracking-widest mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me more about your inquiry..."
                  rows={6}
                  className="w-full resize-none bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-600"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 text-base font-bold uppercase tracking-wider bg-white text-black hover:bg-gray-200"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Email */}
            <div className="bg-[#2a2a2a] border border-gray-800 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-white">
                  <Mail className="h-6 w-6 text-black" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide">Email</h3>
                  <p className="text-gray-400 text-sm">Reach out directly</p>
                </div>
              </div>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-lg text-gray-300 hover:text-white transition-colors block mt-4 font-medium"
              >
                {contactInfo.email}
              </a>
            </div>

            {/* Social Media */}
            <div className="bg-[#2a2a2a] border border-gray-800 p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-wider">Connect on Social Media</h3>
              <div className="space-y-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="flex items-center gap-4 p-4 border border-gray-800 hover:border-gray-600 transition-colors group"
                  >
                    <div className="p-2 bg-[#1a1a1a] group-hover:bg-white transition-colors">
                      <social.icon className="h-5 w-5 text-white group-hover:text-black transition-colors" />
                    </div>
                    <div>
                      <p className="font-medium text-white uppercase text-sm tracking-wider">{social.label}</p>
                      <p className="text-sm text-gray-400">{social.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white text-black p-8">
              <h3 className="text-xl font-bold mb-4 uppercase tracking-wider">Collaborations</h3>
              <p className="text-gray-700 leading-relaxed">
                Available for freelance projects, character design commissions, and creative collaborations. 
                Let's bring your vision to life.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;